import axios from 'axios';
import { API_BASE_URL } from '../config';

const FactCheckService = {
    /**
     * Perform a fact-check analysis
     * @param {string} content - URL, Text, or Base64 Image
     * @param {string} type - 'link', 'text', or 'image'
     * @param {string} token - Clerk session token (from useAuth().getToken())
     */
    async analyze(content, type, token) {
        const payload = {
            type,
            content
        };

        if (!token) {
            console.warn("⚠️ No Clerk token provided. Request will fail authentication.");
        }

        const headers = { "Content-Type": "application/json" };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await axios.post(
            `${API_BASE_URL}/factcheck`,
            payload,
            {
                headers: headers,
                withCredentials: true
            }
        );

        return response.data;
    },

    /**
     * Streaming fact-check (Server-Sent Events over fetch).
     * Calls onProgress({ stage, pct }) as the backend reports progress, and
     * resolves with the final result object. Throws with an axios-like
     * `err.response = { status, data }` on HTTP errors so callers can reuse
     * their 401/402/429 handling.
     *
     * @param {string} content
     * @param {string} type
     * @param {string} token
     * @param {(p:{stage:string,pct:number})=>void} onProgress
     */
    async analyzeStream(content, type, token, onProgress) {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`${API_BASE_URL}/factcheck/stream`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({ type, content }),
        });

        if (!res.ok || !res.body) {
            let data = {};
            try { data = await res.json(); } catch (_) { /* non-JSON error body */ }
            const err = new Error(data?.detail?.message || `Request failed (${res.status})`);
            err.response = { status: res.status, data };
            throw err;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalResult = null;

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const chunks = buffer.split('\n\n');
            buffer = chunks.pop(); // keep the trailing partial chunk
            for (const chunk of chunks) {
                const dataLine = chunk.split('\n').find((l) => l.startsWith('data:'));
                if (!dataLine) continue; // keep-alive comment
                let evt;
                try { evt = JSON.parse(dataLine.slice(5).trim()); } catch (_) { continue; }
                if (evt.type === 'progress') {
                    if (onProgress) onProgress(evt);
                } else if (evt.type === 'result') {
                    finalResult = evt.data;
                } else if (evt.type === 'error') {
                    throw new Error(evt.message || 'Analysis failed.');
                }
            }
        }

        if (finalResult === null) throw new Error('No result received from server.');
        return finalResult;
    },

    /**
     * Check if a string is a valid URL
     */
    isValidUrl(string) {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    }
};

export default FactCheckService;
